import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  fmtTRY, fmtDate, todayISO,
  PAYMENT_METHOD_LABEL, CASH_ACCOUNT_LABEL, PAYMENT_STATUS_LABEL,
} from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { StatusBadge } from "@/components/Common";
import { toast } from "sonner";
import { trackCreate, useOpenedGoal } from "@/lib/metrica";

/**
 * Reusable payments dialog for incomes and expenses.
 * Props:
 *  - open, onOpenChange
 *  - type: "income" | "expense"
 *  - record: the income/expense object
 *  - onUpdated: callback after add/delete (returns the latest record)
 */
export default function PaymentsDialog({ open, onOpenChange, type, record, onUpdated }) {
  useOpenedGoal("payment_create_opened", open);
  const isCollection = type === "job" || type === "income"; // backward compat
  const baseUrl =
    type === "job" ? "jobs" :
    type === "purchase" ? "purchases" :
    type === "income" ? "incomes" : "expenses";
  const title = isCollection ? "Tahsilatlar" : "Ödemeler";
  const partyLabel = isCollection
    ? (record?.customer_name || record?.name)
    : (record?.vendor_name || record?.description);
  const formLabel = isCollection ? "Yeni Tahsilat Ekle" : "Yeni Ödeme Ekle";

  const [current, setCurrent] = useState(record);
  const [form, setForm] = useState({
    date: todayISO(),
    amount: "",
    cash_account: record?.cash_account || "company",
    payment_method: "cash",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  // Invoice summary is derived server-side per request — no client cache.
  // Refetched every time the dialog opens for a fresh record, and again
  // whenever ``current.id`` changes (e.g. parent switched jobs).  A
  // silent 404 (older tenant, endpoint filtered) leaves the box blank.
  const [invoiceSummary, setInvoiceSummary] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!open || !current?.id || type !== "job") {
      setInvoiceSummary(null);
      return () => { cancelled = true; };
    }
    (async () => {
      try {
        const { data } = await api.get(`/jobs/${current.id}/invoice-summary`);
        if (!cancelled) setInvoiceSummary(data);
      } catch {
        if (!cancelled) setInvoiceSummary(null);
      }
    })();
    return () => { cancelled = true; };
  }, [open, current?.id, type]);

  // Faturalı satın alma / iş için TOPLAM = SUM(invoice.grand_total).
  // ``purchase.amount`` ve ``job.amount`` KDV hariç ekonomik değer
  // olduğu için ödeme dialog'unda TOPLAM / KALAN gösterimine uygun
  // değildir.  Backend'de mevcut ``/invoices`` listesi üzerinden
  // ihtiyaç duyulan değer çekilir; ``purchase.amount`` / ``job.amount``
  // DEĞİŞMEZ.  Faturasız kayıtlarda eski davranış (``current.amount``)
  // korunur; expense/income akışları etkilenmez.
  const [invoiceGrandTotalSum, setInvoiceGrandTotalSum] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!open || !current?.id || (type !== "purchase" && type !== "job")) {
      setInvoiceGrandTotalSum(null);
      return () => { cancelled = true; };
    }
    if ((current.invoice_status || "invoiced") !== "invoiced") {
      // Faturasız → mevcut ``current.amount`` kullanılır.
      setInvoiceGrandTotalSum(null);
      return () => { cancelled = true; };
    }
    const params = type === "purchase"
      ? `type=purchase&purchase_id=${encodeURIComponent(current.id)}`
      : `type=sale&job_id=${encodeURIComponent(current.id)}`;
    (async () => {
      try {
        const { data } = await api.get(`/invoices?${params}`);
        if (cancelled) return;
        const sum = (data || []).reduce(
          (s, inv) => s + (Number(inv.grand_total) || 0), 0,
        );
        setInvoiceGrandTotalSum(sum);
      } catch {
        if (!cancelled) setInvoiceGrandTotalSum(null);
      }
    })();
    return () => { cancelled = true; };
  }, [open, current?.id, current?.invoice_status, current?.paid_amount, type]);

  if (!current) return null;

  // Display TOPLAM:
  //   Faturalı purchase / job → SUM(invoice.grand_total)
  //   Diğer / faturasız        → current.amount (mevcut)
  const isInvoicedTotal =
    (type === "purchase" || type === "job") && invoiceGrandTotalSum !== null;
  const displayTotal = isInvoicedTotal
    ? invoiceGrandTotalSum
    : (Number(current.amount) || 0);
  const remaining = Math.max(0, displayTotal - (Number(current.paid_amount) || 0));

  const addPayment = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error("Geçerli bir tutar girin"); return; }
    if (amt > remaining + 0.0001) {
      if (!window.confirm(`Tutar kalan bakiyeden (${fmtTRY(remaining)}) fazla. Yine de eklensin mi?`)) return;
    }
    setSaving(true);
    try {
      const payload = {
        date: form.date,
        amount: amt,
        cash_account: form.cash_account,
        payment_method: form.payment_method,
        notes: form.notes || "",
      };
      const { data } = await trackCreate("payment", () => api.post(`/${baseUrl}/${current.id}/payments`, payload));
      setCurrent(data);
      setForm({
        date: todayISO(),
        amount: "",
        cash_account: form.cash_account,
        payment_method: form.payment_method,
        notes: "",
      });
      toast.success(isCollection ? "Tahsilat eklendi" : "Ödeme eklendi");
      onUpdated?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "İşlem başarısız");
    } finally { setSaving(false); }
  };

  const deletePayment = async (paymentId) => {
    if (!window.confirm("Bu ödeme satırı silinsin mi?")) return;
    try {
      const { data } = await api.delete(`/${baseUrl}/${current.id}/payments/${paymentId}`);
      setCurrent(data);
      toast.success("Silindi");
      onUpdated?.(data);
    } catch {
      toast.error("Silinemedi");
    }
  };

  const payments = (current.payments || []).slice().sort((a, b) => (a.date < b.date ? 1 : -1));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="payments-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">
            {title} — {partyLabel}
          </DialogTitle>
          <div className="text-xs text-slate-500 mt-1">
            {current.description || "—"} • {fmtDate(current.date)}
          </div>
        </DialogHeader>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Mini
            label="Toplam"
            value={fmtTRY(displayTotal)}
            vatBadge={isInvoicedTotal}
            testId="payments-total-mini"
          />
          <Mini label={isCollection ? "Tahsil Edilen" : "Ödenen"} value={fmtTRY(current.paid_amount)} accent="emerald" />
          <Mini label="Kalan" value={fmtTRY(remaining)} accent={remaining > 0 ? "amber" : "slate"} testId="payments-remaining-mini" />
          <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-col gap-1.5 justify-between">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Durum</div>
            <div><StatusBadge value={current.payment_status} label={PAYMENT_STATUS_LABEL[current.payment_status]} /></div>
          </div>
        </div>

        {/* Faturalama Özeti — yalnızca "İş / Satış" bağlamında gösterilir.
            Kaynak: GET /api/jobs/{id}/invoice-summary.  Endpoint her
            dialog açılışında tekrar çağrılır; frontend derived değerleri
            asla local state'te "source of truth" olarak tutmaz.  Rakamlar
            KDV HARİÇ — ekonomik/faturalama ilerlemesini temsil eder. */}
        {type === "job" && (
          current.invoice_status === "invoiced" ? (
            invoiceSummary ? (
              <div className="border border-slate-200 rounded-xl p-4" data-testid="job-invoice-summary">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-medium text-slate-800">Faturalama Özeti</div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500" data-testid="job-invoice-count">
                    Fatura Sayısı: <span className="text-slate-800 font-semibold">{invoiceSummary.invoice_count}</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Mini
                    label="İş Bedeli (KDV Hariç)"
                    value={fmtTRY(invoiceSummary.amount_ex_vat)}
                    testId="job-invoice-amount"
                  />
                  <Mini
                    label="Faturalanan"
                    value={fmtTRY(invoiceSummary.invoiced_ex_vat)}
                    accent="emerald"
                    testId="job-invoice-billed"
                  />
                  <Mini
                    label="Faturalanmamış"
                    value={fmtTRY(invoiceSummary.remaining_ex_vat)}
                    accent={invoiceSummary.remaining_ex_vat > 0 ? "amber" : "slate"}
                    testId="job-invoice-remaining"
                  />
                </div>
                <div className="text-[11px] text-slate-500 mt-2">
                  Rakamlar KDV hariç; müşteri alacağı ve nakit akışı ayrı görüntülenir.
                </div>
              </div>
            ) : null
          ) : (
            <div className="border border-slate-200 rounded-xl p-3 text-xs text-slate-500 bg-slate-50" data-testid="job-invoice-summary-empty">
              Bu iş faturasız olarak işaretlenmiş; faturalama özeti bulunmuyor.
            </div>
          )
        )}

        {/* Add form */}
        <div className="border border-slate-200 rounded-xl p-4">
          <div className="text-sm font-medium text-slate-800 mb-3">{formLabel}</div>
          <form onSubmit={addPayment} className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="add-payment-form">
            <FieldDate label="Tarih *" value={form.date} onChange={(v) => setForm({ ...form, date: v })} testId="payment-date-input" />
            <Field label="Tutar (₺) *" type="number" step="0.01" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} testId="payment-amount-input" required />
            {/* Faturalı kayıtta kasa Şirket'e sabittir; seçici gizlenir ve
                yerine sade bir bilgi rozeti gösterilir.  Backend zaten
                aynı override'ı server-side uygular. */}
            {current.invoice_status === "invoiced" ? (
              <div className="space-y-1.5" data-testid="payment-cash-locked">
                <Label className="text-xs">Kasa</Label>
                <div className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 flex items-center text-sm text-slate-700">
                  Şirket Kasası
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">fatura kuralı</span>
                </div>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label className="text-xs">Kasa</Label>
                <Select value={form.cash_account} onValueChange={(v) => setForm({ ...form, cash_account: v })}>
                  <SelectTrigger className="h-10 rounded-lg" data-testid="payment-cash-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="company">Şirket Kasası</SelectItem>
                    <SelectItem value="personal">Şahsi Kasa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs">Ödeme Yöntemi</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger className="h-10 rounded-lg" data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PAYMENT_METHOD_LABEL ?? {}).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-xs">Notlar</Label>
              <Textarea rows={1} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="md:col-span-3 flex justify-end">
              <Button type="submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="payment-save-button">
                <Plus className="w-4 h-4 mr-1.5" /> {saving ? "Ekleniyor..." : "Ekle"}
              </Button>
            </div>
          </form>
        </div>

        {/* Payments list */}
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/50 text-xs uppercase tracking-wider text-slate-500 font-semibold">
            Ödeme Geçmişi ({(payments ?? []).length})
          </div>
          {(payments ?? []).length === 0 ? (
            <div className="px-6 py-8 text-center text-sm text-slate-500">Henüz ödeme kaydı yok</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-white">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-2.5 font-semibold">Tarih</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Tutar</th>
                  <th className="px-4 py-2.5 font-semibold">Kasa</th>
                  <th className="px-4 py-2.5 font-semibold">Yöntem</th>
                  <th className="px-4 py-2.5 font-semibold">Notlar</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(payments ?? []).map((p) => (
                  <tr key={p.id} data-testid={`payment-row-${p.id}`}>
                    <td className="px-4 py-2.5 whitespace-nowrap">{fmtDate(p.date)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-medium">{fmtTRY(p.amount)}</td>
                    <td className="px-4 py-2.5 text-xs">{CASH_ACCOUNT_LABEL[p.cash_account]}</td>
                    <td className="px-4 py-2.5 text-xs">{PAYMENT_METHOD_LABEL[p.payment_method] || p.payment_method}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-600 max-w-xs truncate">{p.notes || "—"}</td>
                    <td className="px-4 py-2.5 text-right">
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600 hover:bg-red-50" onClick={() => deletePayment(p.id)} data-testid={`payment-delete-${p.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Mini({ label, value, accent = "slate", testId, vatBadge = false }) {
  const accentClass = {
    slate: "text-slate-900",
    emerald: "text-emerald-600",
    amber: "text-amber-600",
  }[accent];
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</div>
      <div className={`font-display text-lg font-bold tracking-tight mt-1 tabular-nums ${accentClass} flex items-center gap-1.5`}>
        <span>{value}</span>
        {vatBadge && (
          <span
            className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium
                       bg-amber-50 text-amber-700 border border-amber-200 uppercase tracking-wider"
            title="KDV Dahil — Faturaların Genel Toplamı"
            data-testid={`${testId || "mini"}-vat-badge`}
          >
            KDV
          </span>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, required, type = "text", step, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input value={value} type={type} step={step} onChange={(e) => onChange(e.target.value)} required={required} className="h-10 rounded-lg" data-testid={testId} />
    </div>
  );
}

function FieldDate({ label, value, onChange, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input type="date" value={value} onChange={(e) => onChange(e.target.value)} className="h-10 rounded-lg" data-testid={testId} />
    </div>
  );
}
